<template>
    <transition>
        <div class="container-fluid page-content">
            <!-- Header Row -->
            <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3 mb-4">
                <div>
                    <h4 class="page-title text-slate-800 fw-bold mb-1">Attribute Values</h4>
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb mb-0">
                            <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                            <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">Master Settings</a></li>
                            <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">Attribute Values List</li>
                        </ol>
                    </nav>
                </div>
                <div>
                    <button type="button" class="btn btn-premium d-flex align-items-center gap-2" @click="toggleModal()">
                        <i class="fas fa-plus"></i> Add New Value
                    </button>
                </div>
            </div>

            <!-- Filter Card -->
            <div class="card border-0 shadow-sm filter-card mb-4">
                <div class="card-body">
                    <div class="row align-items-center justify-content-between gap-3 gap-md-0">
                        <div class="col-12 col-md-auto d-flex align-items-center gap-2">
                            <span class="text-muted text-nowrap">Show</span>
                            <select class="form-select form-select-custom length-select" v-model="tableData.length" @change="fetchData()">
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option :value="pagination.total">All</option>
                            </select>
                            <span class="text-muted text-nowrap">Entries</span>
                        </div>
                        <div class="col-12 col-md-4 d-flex gap-2">
                            <select class="form-select form-select-custom" v-model="tableData.attrbute_id" @change="fetchData()">
                                <option value="">All Attributes</option>
                                <option v-for="(attr, i) in attributesList" :key="i" :value="attr.id">{{ attr.title }}</option>
                            </select>
                            <div class="search-input-wrapper flex-grow-1">
                                <input type="text" class="form-control form-control-custom search-input" v-model="tableData.search" placeholder="Search values..." @input="fetchData()">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Table Card -->
            <div class="card border-0 shadow-sm table-card">
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <table class="table table-premium mb-0" v-if="!loading && items.length > 0">
                            <thead>
                                <tr>
                                    <th style="width: 80px;">SL</th>
                                    <th>Attribute</th>
                                    <th>Value</th>
                                    <th class="text-end" style="width: 120px;">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, i) in items" :key="i">
                                    <td>{{ pagination.from + i }}</td>
                                    <td class="fw-bold text-slate-800">
                                        <span class="badge bg-light text-dark border">{{ item.attribute ? item.attribute.title : 'N/A' }}</span>
                                    </td>
                                    <td>{{ item.value }}</td>
                                    <td class="text-end">
                                        <div class="dropdown d-inline-block">
                                            <button class="btn btn-action-dropdown dropdown-toggle arrow-none" data-bs-toggle="dropdown" aria-expanded="false">
                                                <i class="fas fa-ellipsis-v"></i>
                                            </button>
                                            <div class="dropdown-menu dropdown-menu-end shadow-sm border-0">
                                                <a href="javascript:void(0);" class="dropdown-item text-warning" @click="edit(item)">
                                                    <i class="fas fa-edit me-2"></i>Edit
                                                </a>
                                                <a href="javascript:void(0);" class="dropdown-item text-danger" @click="deleteItem(item)">
                                                    <i class="fas fa-trash-alt me-2"></i>Delete
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Empty State -->
                        <div class="text-center py-5" v-if="!loading && items.length === 0">
                            <div class="empty-state-icon mb-3">
                                <i class="fas fa-tags text-muted fa-3x"></i>
                            </div>
                            <h5 class="text-slate-700 fw-bold">No Attribute Values Found</h5>
                            <p class="text-muted mb-0">Try adjusting your search or add a new attribute value.</p>
                        </div>

                        <!-- Loading State -->
                        <div class="text-center py-5" v-if="loading">
                            <div class="spinner-premium mb-3 mx-auto"></div>
                            <h6 class="text-indigo fw-semibold">Loading data...</h6>
                        </div>
                    </div>
                </div>

                <!-- Table Footer with Pagination -->
                <div class="card-footer bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3" v-if="!loading && items.length > 0">
                    <div class="text-muted small">
                        Showing {{ pagination.from }} to {{ pagination.to }} of {{ pagination.total }} entries
                    </div>
                    <Pagination :pagination="pagination" :language="lang" @onChangePage="setPage"></Pagination>
                </div>
            </div>

            <!-- Modal -->
            <Modal @close="toggleModal()" :modalActive="modalActive">
                <div class="modal-content border-0 shadow-lg modal-premium modal-premium-sm">
                    <div class="modal-header border-0 pb-0">
                        <h4 class="fw-bold m-0 text-slate-800">{{ editMode ? 'Update Attribute Value' : 'Create New Value' }}</h4>
                        <button @click="toggleModal()" type="button" class="btn-close-custom">✕</button>
                    </div>
                    <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                        <div class="modal-body py-4">
                            <div class="mb-3">
                                <label for="attrbute_id" class="form-label-custom text-slate-700 fw-semibold mb-2">Attribute *</label>
                                <select class="form-select form-select-custom" :class="{'is-invalid': errors.attrbute_id}" v-model="form.attrbute_id" id="attrbute_id" @change="onkeyPress('attrbute_id')">
                                    <option value="">Select Attribute</option>
                                    <option v-for="(attr, i) in attributesList" :key="i" :value="attr.id">{{ attr.title }}</option>
                                </select>
                                <div class="invalid-feedback" v-if="errors.attrbute_id">
                                    {{ errors.attrbute_id[0] }}
                                </div>
                            </div>
                            <div class="mb-3">
                                <label for="value" class="form-label-custom text-slate-700 fw-semibold mb-2">Attribute Value *</label>
                                <input type="text" class="form-control form-control-custom" :class="{'is-invalid': errors.value}" @keypress="onkeyPress('value')" v-model="form.value" id="value" placeholder="Enter attribute value" autocomplete="off">
                                <div class="invalid-feedback" v-if="errors.value">
                                    {{ errors.value[0] }}
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer border-0 pt-0 justify-content-end gap-2">
                            <button type="button" class="btn btn-outline-premium px-4" @click="toggleModal()">Cancel</button>
                            <button type="submit" class="btn btn-premium px-4" :disabled="disabled">
                                <span v-show="isSubmit" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {{ btn }}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    </transition>
</template>

<script>

import Modal from "../helper/Modal.vue";
import { ref, onMounted } from "vue";
import Pagination from '@/components/Pagination.vue';
import Form from "vform";
import axios from "axios";
export default {
    name: "PosLeftbar",
    components: {
        Modal,
        Pagination
    },
    props:{
        language: {
          type: Object,
          default: () => {
            return {
              lengthMenu: null,
              info: null,
              zeroRecords: null, 
              search: null
            }
          },
        },
    },
    data() {
        return {
            isSubmit: false,
            showModal: false,
            editMode:false,
            disabled: false,
            modalActive:false,
            errors: {},
            btn:'Create',
            loading: true,
            items: [],
            attributesList: [],
            companies: [],
            form: new Form({
                id: '',
                attrbute_id: '',
                value: '',
            }),
            tableData: {
                draw: 0,
                length: 10,
                search: '',
                attrbute_id: '',
                column: 0,
                dir: 'desc',
            }, 
            lang: {
                lengthMenu: this.$props.language?.lengthMenu ? this.$props.language.lengthMenu : 'Show_MENU_entries',
                info: this.$props.language?.info ? this.$props.language.info : 'Showing_FROM_to_TO_of_TOTAL_entries',
                zeroRecords: this.$props.language?.zeroRecords ? this.$props.language.zeroRecords : 'No data available in table.', 
                search: this.$props.language?.search ? this.$props.language.search : 'Search'
            },
            pagination: {
                lastPage: '',
                currentPage: '',
                total: '',
                lastPageUrl: '',
                nextPageUrl: '',
                prevPageUrl: '',
                from: '',
                to: '',
                links:[],
            }
        }
    },
    created() {
        this.fetchData(); 
        this.fetchAttributes();
    },

    methods: {
        toggleModal: function() {
            this.modalActive = !this.modalActive;  
            console.log('this.modalActive', this.modalActive)
            if(!this.modalActive){
                this.editMode = false;
                this.btn='Create';
            } 
            this.errors = '';
            this.isSubmit = false;
            this.form.reset(); 
            console.log('then',this.isSubmit)
        },
        fetchAttributes() {
            axios.get(this.apiUrl+'/attributes', this.headerjson)
            .then((res) => { 
                this.attributesList = res.data.data;
            });
        },
        fetchData(url = this.apiUrl+'/attribute_values') { 
            this.loading = true;
            axios.get(url, {params:this.tableData, headers: this.headerparams})
            .then((res) => { 
                this.items = res.data.data;
                this.configPagination(res.data);
            })
            .finally((ress) => {
              this.loading = false; 
            });
        }, 
        configPagination(data){
            this.pagination.lastPage = data.last_page;
            this.pagination.currentPage = data.current_page;
            this.pagination.total   = data.total ? data.total : 0;
            this.pagination.lastPageUrl = data.last_page_url;
            this.pagination.nextPageUrl = data.next_page_url;
            this.pagination.prevPageUrl = data.prev_page_url;
            this.pagination.from = data.from ? data.from : 0;
            this.pagination.to = data.to ? data.to : 0;  
            this.pagination.links = data.links;
        },
        setPage(data){  
            this.fetchData(data.url); 
        },

        edit: function(item) {
            this.toggleModal(); 
            this.btn='Update';
            this.editMode = true;
            
            this.form.fill(item); 
        },
        submitForm: function(e) { 
            
            this.isSubmit = true;
            this.disabled = true;
            const formData = new FormData();  
            formData.append('attrbute_id', this.form.attrbute_id);
            formData.append('value', this.form.value); 
            if(this.editMode){
                formData.append('_method', 'put');
                var postEvent = axios.post(this.apiUrl+'/attribute_values/'+this.form.id, formData, this.headers);
            }else{  
                var postEvent = axios.post(this.apiUrl+'/attribute_values', formData, this.headers);
            }           

            postEvent.then(res => {
                
                this.isSubmit = false;
                this.disabled = false;
                if(res.status == 200){
                    this.toggleModal();
                    this.fetchData();
                    this.$toast.success(res.data.message); 
                }else{
                    this.$toast.error(res.data.message);
                }
                console.log(res.data)
            }).catch(err => {  
                this.isSubmit = false;
                this.disabled = false;
                this.$toast.error(err.response.data.message);
                if(err.response.status == 422){
                    this.errors = err.response.data.errors 
                }
            })
        },

        onkeyPress: function(field) { 
            for (var k in this.errors){     // Loop through the object
                if(k==field){      // If the current key contains the string we're looking for 
                    delete this.errors[k];  // Delete obj[key];
                }
            }  
        },
        deleteItem: function(item) {
            this.$swal({
                title: 'Are you sure?',
                text: "You want delete this item!", 
                showCancelButton: true,
                confirmButtonCategory: '#3085d6',
                cancelButtonCategory: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => { 
                if (result.value) { 
                    axios.delete(this.apiUrl+'/attribute_values/'+item.id, this.headerjson)
                    .then(res => {
                        if(res.status == 200){ 
                            this.fetchData();
                            this.$toast.success(res.data.message); 
                        }else{
                            this.$toast.error(res.data.message);
                        }
                        console.log(res.data)
                    }).catch(err => {  
                        this.$toast.error(err.response.data.message);
                        if(err.response.status == 422){
                            this.errors = err.response.data.errors 
                        }
                    }) 
                }else{
                    // this.$toast.error(`Hey! I'm here`);
                    // this.$toast.warning(`Hey! I'm here`);
                    // this.$toast.info(`Hey! I'm here`)
                }
            }); 
        },
    },
    destroyed() {},
    mounted() {
        window.scrollTo(0, 0);
    },
    computed: {}
}
</script>

<style scoped>
.modal-content.scrollbar-width-thin { 
    width: 600px;
}

label {
    display: inline-block;
    margin: 0px 0px 4px 2px;
    float: left;
}

.invalid-feedback {
    display: block;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 80%;
    color: #dc3545;
    text-align: left;
}
</style>